// Shared resolution of a doc's `draeger.com/docs-icon` annotation to an <img>-able URL.
//
// One implementation for every place an icon is rendered — the app's entity cards
// (entityHelpers.docsIcon), the landscape tiles, and the backend's landscape logo seed —
// so the accepted forms can't drift apart. Framework-free (no React), so the backend can
// import it from the package's main entry.
//
// Accepted forms (catalog-info.yaml → metadata.annotations):
//   draeger.com/docs-icon: assets/logo.png             an image in the doc's own docs/assets/
//                          (also ./assets/… or docs/assets/…)
//   draeger.com/docs-icon: simpleicons:sonarqube       a Simple Icons slug (bare `sonarqube` too)
//   draeger.com/docs-icon: https://…/logo.png          any image URL
//   draeger.com/docs-icon: /icons/under-construction.svg   a same-origin app asset

export const ANNOTATION_DOCS_ICON = 'draeger.com/docs-icon';

/** The deliberately unfinished default icon the doc starter-kit template stamps on a new doc. */
export const PLACEHOLDER_DOCS_ICON = '/icons/under-construction.svg';

/** Where the TechDocs backend lives relative to the app (same origin when deployed). */
export const TECHDOCS_BASE_PATH = '/api/techdocs';

const IMAGE_EXT = /\.(png|jpe?g|gif|webp|svg)$/i;

/** The entity a repo-relative icon belongs to (the doc whose TechDocs site holds it). */
export interface DocsIconEntityRef {
  kind: string;
  namespace?: string;
  name: string;
}

/**
 * For a docs-icon value that points into the doc's OWN repo (`assets/logo.png`,
 * `./assets/logo.png` or `docs/assets/logo.png`), the file's path inside the built
 * TechDocs site — or null when the value is not such a path, or is one we refuse.
 *
 * TechDocs publishes `docs/` as the site root, so `docs/assets/logo.png` is served as
 * `assets/logo.png`. Only plain relative paths below `assets/` with an image extension
 * pass: no `.`/`..`/empty segments, no backslashes, schemes, spaces, or query, fragment
 * or percent-encoding tricks.
 */
export function techdocsIconPath(value: string | undefined | null): string | null {
  let v = (value || '').trim();
  if (v.startsWith('./')) v = v.slice(2);
  if (v.startsWith('docs/')) v = v.slice('docs/'.length);
  if (!v.startsWith('assets/')) return null;
  if (!/^[A-Za-z0-9._\-/]+$/.test(v)) return null;
  if (v.split('/').some(seg => seg === '' || seg === '.' || seg === '..')) return null;
  if (!IMAGE_EXT.test(v)) return null;
  return v;
}

/**
 * True for a value written as a repo-relative path. Such a value is either a valid
 * TechDocs asset (see techdocsIconPath) or refused — it must never become a slug.
 */
export function isRepoRelativeDocsIcon(value: string | undefined | null): boolean {
  const v = (value || '').trim();
  if (!v || /^https?:\/\//i.test(v) || v.startsWith('/')) return false;
  return v.includes('/') || v.includes('\\') || v.includes('..');
}

/**
 * Resolve a docs-icon value to a URL, or null to fall back to a glyph.
 *
 * A repo-relative image resolves to the entity's own TechDocs static file,
 * `<techdocsBase>/static/docs/<namespace>/<kind>/<name>/assets/…`. That needs `entity`
 * (without it the value resolves to null), the TechDocs user cookie in the browser (see
 * useTechDocsCookie in ./ui), and the doc's first TechDocs build — so renderers keep
 * their glyph fallback for when the image fails to load. `techdocsBase` defaults to the
 * same-origin `/api/techdocs`; pass discoveryApi's `getBaseUrl('techdocs')` where the
 * backend is on another origin (local dev).
 */
export function resolveDocsIcon(
  raw: string | undefined | null,
  entity?: DocsIconEntityRef,
  techdocsBase: string = TECHDOCS_BASE_PATH,
): string | null {
  const v = (raw || '').trim();
  if (!v) return null;
  if (/^https?:\/\//i.test(v)) return v; // any image URL
  if (v.startsWith('/')) return v; // self-hosted (same-origin) asset
  const assetPath = techdocsIconPath(v);
  if (assetPath) {
    if (!entity) return null;
    const ref = [entity.namespace || 'default', entity.kind.toLowerCase(), entity.name]
      .map(encodeURIComponent)
      .join('/');
    return `${techdocsBase.replace(/\/+$/, '')}/static/docs/${ref}/${assetPath}`;
  }
  if (isRepoRelativeDocsIcon(v)) return null; // a refused repo path, not a slug
  const slug = v.startsWith('simpleicons:') ? v.slice('simpleicons:'.length) : v;
  return `https://cdn.simpleicons.org/${encodeURIComponent(slug)}`;
}
